import React, { useState, useEffect } from "react";
import api from "../services/api";  // Use shared api.js with auto-switching

export default function ReconciliationReport() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reportGenerated, setReportGenerated] = useState(false);
  const [activeTab, setActiveTab] = useState("transactions");
  const [summary, setSummary] = useState({
    totalTransactions: 0,
    totalDebits: 0,
    totalCredits: 0,
    netMovement: 0
  });

  // Set default date range (last 30 days)
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    setEndDate(today.toISOString().split("T")[0]);
    setStartDate(thirtyDaysAgo.toISOString().split("T")[0]);
  }, []);

  const fetchReport = async () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Fetch transactions
      const txnRes = await api.get(`/Transaction/all?startDate=${startDate}&endDate=${endDate}`);
      setTransactions(txnRes.data || []);

      // Fetch ledger entries
      const ledgerRes = await api.get(`/Accounting/journal-entries?startDate=${startDate}&endDate=${endDate}`);
      setLedgerEntries(ledgerRes.data || []);

      // Calculate summary
      const txns = txnRes.data || [];
      const totalDebits = txns.filter(t => t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0);
      const totalCredits = txns.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);

      setSummary({
        totalTransactions: txns.length,
        totalDebits,
        totalCredits,
        netMovement: totalCredits - totalDebits
      });

      setReportGenerated(true);
    } catch (err) {
      console.error("Error fetching report:", err);
      setError(err.response?.data?.message || "Failed to fetch report data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₦${Math.abs(Number(amount || 0)).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-NG", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Export to Excel
  const exportToExcel = () => {
    // Helper to strip currency symbols and get plain number
    const toPlainNumber = (val) => {
      if (val === null || val === undefined) return 0;
      const cleaned = String(val).replace(/[₦NGN,\s]/gi, '').trim();
      const num = parseFloat(cleaned);
      return isNaN(num) ? 0 : num;
    };

    // Create workbook content as CSV
    let csvContent = "";

    // Summary Sheet
    csvContent += "RECONCILIATION REPORT\n";
    csvContent += `Report Period: ${startDate} to ${endDate}\n`;
    csvContent += `Generated: ${new Date().toLocaleString()}\n\n`;

    csvContent += "SUMMARY\n";
    csvContent += `Total Transactions,${summary.totalTransactions}\n`;
    csvContent += `Total Credits,${toPlainNumber(summary.totalCredits).toFixed(2)}\n`;
    csvContent += `Total Debits,${toPlainNumber(summary.totalDebits).toFixed(2)}\n`;
    csvContent += `Net Movement,${toPlainNumber(summary.netMovement).toFixed(2)}\n\n`;

    // Transactions
    csvContent += "CUSTOMER TRANSACTIONS\n";
    csvContent += "Date,Account Number,Account Name,Type,Amount,Balance After,Description,Status\n";
    transactions.forEach(txn => {
      const description = (txn.description || "").replace(/[₦]/g, 'N').replace(/"/g, '""');
      csvContent += `"${formatDate(txn.createdAt)}","${txn.accountNumber || ""}","${txn.accountName || ""}","${txn.type}",${toPlainNumber(txn.amount).toFixed(2)},${toPlainNumber(txn.balanceAfter).toFixed(2)},"${description}","${txn.status}"\n`;
    });

    csvContent += "\nLEDGER ENTRIES\n";
    csvContent += "Date,Journal Number,Account Code,Account Name,Debit,Credit,Description\n";
    ledgerEntries.forEach(entry => {
      if (entry.lines) {
        entry.lines.forEach(line => {
          const description = (entry.description || "").replace(/[₦]/g, 'N').replace(/"/g, '""');
          csvContent += `"${formatDate(entry.createdAt)}","${entry.journalNumber}","${line.accountCode}","${line.accountName}",${toPlainNumber(line.debit).toFixed(2)},${toPlainNumber(line.credit).toFixed(2)},"${description}"\n`;
        });
      }
    });

    // Download with BOM for Excel
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Reconciliation_Report_${startDate}_to_${endDate}.csv`;
    link.click();
  };

  // Export to PDF
  const exportToPDF = () => {
    // Create PDF-friendly HTML
    const pdfHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Reconciliation Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
          h1 { color: #1a73e8; font-size: 24px; margin-bottom: 5px; }
          h2 { color: #333; font-size: 16px; margin-top: 20px; border-bottom: 2px solid #1a73e8; padding-bottom: 5px; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #1a73e8; padding-bottom: 20px; }
          .meta { color: #666; font-size: 11px; }
          .summary-grid { display: flex; justify-content: space-between; margin: 20px 0; }
          .summary-box { background: #f5f5f5; padding: 15px; border-radius: 8px; text-align: center; width: 22%; }
          .summary-label { font-size: 11px; color: #666; }
          .summary-value { font-size: 18px; font-weight: bold; color: #333; margin-top: 5px; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 10px; }
          th { background: #1a73e8; color: white; padding: 8px; text-align: left; }
          td { padding: 6px 8px; border-bottom: 1px solid #ddd; }
          tr:nth-child(even) { background: #f9f9f9; }
          .credit { color: #28a745; }
          .debit { color: #dc3545; }
          .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 10px; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🏦 UPTECH BANKING</h1>
          <h2 style="border: none;">Reconciliation Report</h2>
          <p class="meta">Period: ${startDate} to ${endDate}</p>
          <p class="meta">Generated: ${new Date().toLocaleString()}</p>
        </div>

        <h2>Summary</h2>
        <div class="summary-grid">
          <div class="summary-box">
            <div class="summary-label">Total Transactions</div>
            <div class="summary-value">${summary.totalTransactions}</div>
          </div>
          <div class="summary-box">
            <div class="summary-label">Total Credits</div>
            <div class="summary-value credit">${formatCurrency(summary.totalCredits)}</div>
          </div>
          <div class="summary-box">
            <div class="summary-label">Total Debits</div>
            <div class="summary-value debit">${formatCurrency(summary.totalDebits)}</div>
          </div>
          <div class="summary-box">
            <div class="summary-label">Net Movement</div>
            <div class="summary-value">${formatCurrency(summary.netMovement)}</div>
          </div>
        </div>

        <h2>Customer Account Transactions</h2>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Account</th>
              <th>Name</th>
              <th>Type</th>
              <th>Amount</th>
              <th>Balance</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            ${transactions.map(txn => `
              <tr>
                <td>${formatDate(txn.createdAt)}</td>
                <td>${txn.accountNumber || "-"}</td>
                <td>${txn.accountName || "-"}</td>
                <td>${txn.type}</td>
                <td class="${txn.amount >= 0 ? 'credit' : 'debit'}">${txn.amount >= 0 ? '+' : ''}${formatCurrency(txn.amount)}</td>
                <td>${formatCurrency(txn.balanceAfter)}</td>
                <td>${txn.description || "-"}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>

        <h2>Ledger Journal Entries</h2>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Journal #</th>
              <th>Account</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            ${ledgerEntries.map(entry =>
              entry.lines ? entry.lines.map(line => `
                <tr>
                  <td>${formatDate(entry.createdAt)}</td>
                  <td>${entry.journalNumber}</td>
                  <td>${line.accountCode} - ${line.accountName}</td>
                  <td class="debit">${line.debit ? formatCurrency(line.debit) : "-"}</td>
                  <td class="credit">${line.credit ? formatCurrency(line.credit) : "-"}</td>
                  <td>${entry.description || "-"}</td>
                </tr>
              `).join("") : ""
            ).join("")}
          </tbody>
        </table>

        <div class="footer">
          <p>This is a computer-generated report from Uptech Banking System</p>
          <p>Report ID: REC-${Date.now()}</p>
        </div>
      </body>
      </html>
    `;

    // Open new window and print as PDF
    const printWindow = window.open("", "_blank");
    printWindow.document.write(pdfHTML);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Print directly
  const handlePrint = () => {
    exportToPDF(); // Uses same method - browser print dialog allows printing
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>📊 Reconciliation Report</h1>
        <p style={styles.subtitle}>Download customer transactions and ledger entries for reconciliation</p>
      </div>

      {/* Date Range Selection */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Select Date Range</h3>
        <div style={styles.dateRow}>
          <div style={styles.dateGroup}>
            <label style={styles.label}>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={styles.dateInput}
            />
          </div>
          <div style={styles.dateGroup}>
            <label style={styles.label}>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={styles.dateInput}
            />
          </div>
          <button
            onClick={fetchReport}
            disabled={loading}
            style={styles.generateBtn}
          >
            {loading ? "Generating..." : "🔍 Generate Report"}
          </button>
        </div>

        {/* Quick Date Presets */}
        <div style={styles.presets}>
          <span style={styles.presetLabel}>Quick Select:</span>
          <button
            style={styles.presetBtn}
            onClick={() => {
              const today = new Date();
              setStartDate(today.toISOString().split("T")[0]);
              setEndDate(today.toISOString().split("T")[0]);
            }}
          >
            Today
          </button>
          <button
            style={styles.presetBtn}
            onClick={() => {
              const today = new Date();
              const weekAgo = new Date(today);
              weekAgo.setDate(today.getDate() - 7);
              setStartDate(weekAgo.toISOString().split("T")[0]);
              setEndDate(today.toISOString().split("T")[0]);
            }}
          >
            Last 7 Days
          </button>
          <button
            style={styles.presetBtn}
            onClick={() => {
              const today = new Date();
              const monthAgo = new Date(today);
              monthAgo.setDate(today.getDate() - 30);
              setStartDate(monthAgo.toISOString().split("T")[0]);
              setEndDate(today.toISOString().split("T")[0]);
            }}
          >
            Last 30 Days
          </button>
          <button
            style={styles.presetBtn}
            onClick={() => {
              const today = new Date();
              const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
              setStartDate(firstDay.toISOString().split("T")[0]);
              setEndDate(today.toISOString().split("T")[0]);
            }}
          >
            This Month
          </button>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Report Content */}
      {reportGenerated && (
        <div id="report-content">
          {/* Summary Cards */}
          <div style={styles.summaryRow}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryIcon}>📊</div>
              <div style={styles.summaryLabel}>Total Transactions</div>
              <div style={styles.summaryValue}>{summary.totalTransactions}</div>
            </div>
            <div style={{ ...styles.summaryCard, borderTopColor: "#28a745" }}>
              <div style={styles.summaryIcon}>📈</div>
              <div style={styles.summaryLabel}>Total Credits</div>
              <div style={{ ...styles.summaryValue, color: "#28a745" }}>{formatCurrency(summary.totalCredits)}</div>
            </div>
            <div style={{ ...styles.summaryCard, borderTopColor: "#dc3545" }}>
              <div style={styles.summaryIcon}>📉</div>
              <div style={styles.summaryLabel}>Total Debits</div>
              <div style={{ ...styles.summaryValue, color: "#dc3545" }}>{formatCurrency(summary.totalDebits)}</div>
            </div>
            <div style={{ ...styles.summaryCard, borderTopColor: "#17a2b8" }}>
              <div style={styles.summaryIcon}>💰</div>
              <div style={styles.summaryLabel}>Net Movement</div>
              <div style={{ ...styles.summaryValue, color: "#17a2b8" }}>{formatCurrency(summary.netMovement)}</div>
            </div>
          </div>

          {/* Export Buttons */}
          <div style={styles.exportRow}>
            <button onClick={exportToExcel} style={styles.exportBtn}>
              <span style={styles.exportIcon}>📗</span> Export to Excel
            </button>
            <button onClick={exportToPDF} style={{ ...styles.exportBtn, backgroundColor: "#dc3545" }}>
              <span style={styles.exportIcon}>📕</span> Export to PDF
            </button>
            <button onClick={handlePrint} style={{ ...styles.exportBtn, backgroundColor: "#6c757d" }}>
              <span style={styles.exportIcon}>🖨️</span> Print Report
            </button>
          </div>

          {/* Tabs */}
          <div style={styles.tabs}>
            <button
              style={activeTab === "transactions" ? styles.tabActive : styles.tab}
              onClick={() => setActiveTab("transactions")}
            >
              Customer Transactions ({transactions.length})
            </button>
            <button
              style={activeTab === "ledger" ? styles.tabActive : styles.tab}
              onClick={() => setActiveTab("ledger")}
            >
              Ledger Entries ({ledgerEntries.length})
            </button>
          </div>

          {/* Transactions Table */}
          {activeTab === "transactions" && (
            <div style={styles.tableCard}>
              <h3 style={styles.tableTitle}>Customer Account Transactions</h3>
              {transactions.length === 0 ? (
                <p style={styles.noData}>No transactions found for the selected period</p>
              ) : (
                <div style={styles.tableWrapper}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Date</th>
                        <th style={styles.th}>Account #</th>
                        <th style={styles.th}>Account Name</th>
                        <th style={styles.th}>Type</th>
                        <th style={styles.th}>Amount</th>
                        <th style={styles.th}>Balance After</th>
                        <th style={styles.th}>Description</th>
                        <th style={styles.th}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((txn, idx) => (
                        <tr key={idx} style={idx % 2 === 0 ? styles.trEven : {}}>
                          <td style={styles.td}>{formatDate(txn.createdAt)}</td>
                          <td style={styles.td}>{txn.accountNumber || "-"}</td>
                          <td style={styles.td}>{txn.accountName || "-"}</td>
                          <td style={styles.td}>
                            <span style={{
                              ...styles.badge,
                              backgroundColor: txn.type?.includes("Deposit") || txn.type?.includes("In")
                                ? "#d4edda"
                                : txn.type?.includes("Withdrawal") || txn.type?.includes("Out")
                                  ? "#f8d7da"
                                  : "#e2e3e5",
                              color: txn.type?.includes("Deposit") || txn.type?.includes("In")
                                ? "#155724"
                                : txn.type?.includes("Withdrawal") || txn.type?.includes("Out")
                                  ? "#721c24"
                                  : "#383d41"
                            }}>
                              {txn.type}
                            </span>
                          </td>
                          <td style={{
                            ...styles.td,
                            color: txn.amount >= 0 ? "#28a745" : "#dc3545",
                            fontWeight: "600"
                          }}>
                            {txn.amount >= 0 ? "+" : ""}{formatCurrency(txn.amount)}
                          </td>
                          <td style={styles.td}>{formatCurrency(txn.balanceAfter)}</td>
                          <td style={styles.td}>{txn.description || "-"}</td>
                          <td style={styles.td}>
                            <span style={{
                              ...styles.statusBadge,
                              backgroundColor: txn.status === "Completed" ? "#28a745" : "#ffc107"
                            }}>
                              {txn.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Ledger Entries Table */}
          {activeTab === "ledger" && (
            <div style={styles.tableCard}>
              <h3 style={styles.tableTitle}>Ledger Journal Entries</h3>
              {ledgerEntries.length === 0 ? (
                <p style={styles.noData}>No ledger entries found for the selected period</p>
              ) : (
                <div style={styles.tableWrapper}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Date</th>
                        <th style={styles.th}>Journal #</th>
                        <th style={styles.th}>Account Code</th>
                        <th style={styles.th}>Account Name</th>
                        <th style={styles.th}>Debit</th>
                        <th style={styles.th}>Credit</th>
                        <th style={styles.th}>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerEntries.map((entry, idx) => (
                        entry.lines ? entry.lines.map((line, lineIdx) => (
                          <tr key={`${idx}-${lineIdx}`} style={(idx + lineIdx) % 2 === 0 ? styles.trEven : {}}>
                            <td style={styles.td}>{formatDate(entry.createdAt)}</td>
                            <td style={styles.td}>
                              <span style={styles.journalBadge}>{entry.journalNumber}</span>
                            </td>
                            <td style={styles.td}>{line.accountCode}</td>
                            <td style={styles.td}>{line.accountName}</td>
                            <td style={{ ...styles.td, color: line.debit ? "#dc3545" : "#ccc", fontWeight: line.debit ? "600" : "400" }}>
                              {line.debit ? formatCurrency(line.debit) : "-"}
                            </td>
                            <td style={{ ...styles.td, color: line.credit ? "#28a745" : "#ccc", fontWeight: line.credit ? "600" : "400" }}>
                              {line.credit ? formatCurrency(line.credit) : "-"}
                            </td>
                            <td style={styles.td}>{entry.description || "-"}</td>
                          </tr>
                        )) : null
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Report Footer */}
          <div style={styles.reportFooter}>
            <p>Report generated on {new Date().toLocaleString()}</p>
            <p>Report ID: REC-{Date.now()}</p>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: "20px",
    maxWidth: "1400px",
    margin: "0 auto",
    backgroundColor: "#f5f7fa",
    minHeight: "100vh"
  },
  header: {
    marginBottom: "20px"
  },
  title: {
    fontSize: "28px",
    fontWeight: "bold",
    color: "#1a73e8",
    marginBottom: "5px"
  },
  subtitle: {
    color: "#666",
    fontSize: "14px"
  },
  card: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    marginBottom: "20px"
  },
  cardTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "15px"
  },
  dateRow: {
    display: "flex",
    gap: "20px",
    alignItems: "flex-end",
    flexWrap: "wrap"
  },
  dateGroup: {
    display: "flex",
    flexDirection: "column"
  },
  label: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#666",
    marginBottom: "5px"
  },
  dateInput: {
    padding: "10px 15px",
    fontSize: "14px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    minWidth: "150px"
  },
  generateBtn: {
    padding: "12px 24px",
    fontSize: "14px",
    fontWeight: "600",
    backgroundColor: "#1a73e8",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer"
  },
  presets: {
    marginTop: "15px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap"
  },
  presetLabel: {
    fontSize: "12px",
    color: "#666"
  },
  presetBtn: {
    padding: "6px 12px",
    fontSize: "12px",
    backgroundColor: "#f0f0f0",
    border: "1px solid #ddd",
    borderRadius: "20px",
    cursor: "pointer"
  },
  error: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "12px 15px",
    borderRadius: "8px",
    marginBottom: "20px"
  },
  summaryRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "15px",
    marginBottom: "20px"
  },
  summaryCard: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "20px",
    textAlign: "center",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    borderTop: "4px solid #1a73e8"
  },
  summaryIcon: {
    fontSize: "24px",
    marginBottom: "10px"
  },
  summaryLabel: {
    fontSize: "12px",
    color: "#666",
    marginBottom: "5px"
  },
  summaryValue: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#333"
  },
  exportRow: {
    display: "flex",
    gap: "10px",
    marginBottom: "20px",
    flexWrap: "wrap"
  },
  exportBtn: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 20px",
    fontSize: "14px",
    fontWeight: "600",
    backgroundColor: "#28a745",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer"
  },
  exportIcon: {
    fontSize: "16px"
  },
  tabs: {
    display: "flex",
    gap: "10px",
    marginBottom: "20px"
  },
  tab: {
    padding: "12px 24px",
    fontSize: "14px",
    fontWeight: "500",
    backgroundColor: "#f0f0f0",
    color: "#666",
    border: "none",
    borderRadius: "8px 8px 0 0",
    cursor: "pointer"
  },
  tabActive: {
    padding: "12px 24px",
    fontSize: "14px",
    fontWeight: "600",
    backgroundColor: "white",
    color: "#1a73e8",
    border: "none",
    borderRadius: "8px 8px 0 0",
    cursor: "pointer",
    borderBottom: "3px solid #1a73e8"
  },
  tableCard: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    marginBottom: "20px"
  },
  tableTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "15px"
  },
  tableWrapper: {
    overflowX: "auto"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px"
  },
  th: {
    backgroundColor: "#1a73e8",
    color: "white",
    padding: "12px 10px",
    textAlign: "left",
    fontWeight: "600",
    whiteSpace: "nowrap"
  },
  td: {
    padding: "10px",
    borderBottom: "1px solid #eee",
    color: "#333"
  },
  trEven: {
    backgroundColor: "#f9f9f9"
  },
  badge: {
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "11px",
    fontWeight: "600"
  },
  statusBadge: {
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "11px",
    fontWeight: "600",
    color: "white"
  },
  journalBadge: {
    backgroundColor: "#e3f2fd",
    color: "#1565c0",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "11px",
    fontWeight: "600",
    fontFamily: "monospace"
  },
  noData: {
    textAlign: "center",
    color: "#666",
    padding: "40px"
  },
  reportFooter: {
    textAlign: "center",
    color: "#999",
    fontSize: "12px",
    padding: "20px",
    borderTop: "1px solid #eee"
  }
};
